/**
 * Keep the screen awake while the display runs.
 *
 * The browser drops a screen wake lock whenever the page is hidden, so the
 * display asks again each time the page becomes visible. The system can also
 * drop the lock while the page stays visible, and then the display asks again
 * at once. A refused request retries after five minutes while visible.
 * A browser without the API leaves the display as it is. The TV's power plan
 * is the backstop.
 *
 * @param nav - The navigator to ask.
 * @param doc - The document whose visibility re-arms the lock.
 * @param timer - The retry timer, injectable for tests.
 * @returns A function that releases the lock and stops asking.
 */
export function holdWakeLock(
  nav: Navigator,
  doc: Document,
  timer: WakeLockTimer = systemTimer
): () => void {
  if (typeof nav.wakeLock?.request !== 'function') return () => undefined

  let cancelRetry: (() => void) | undefined
  let held: WakeLockSentinel | null = null
  let asking = false
  let stopped = false

  const ask = (): void => {
    if (stopped || asking || held !== null) return
    cancelRetry?.()
    cancelRetry = undefined
    asking = true
    void Promise.resolve()
      .then(() => nav.wakeLock.request('screen'))
      .then(
        sentinel => {
          asking = false
          if (stopped) {
            void sentinel.release().catch(() => undefined)
            return
          }
          held = sentinel
          sentinel.addEventListener(
            'release',
            () => {
              if (held === sentinel) held = null
              askIfVisible()
            },
            { once: true }
          )
        },
        () => {
          asking = false
          if (!stopped) {
            cancelRetry = timer.after(WAKE_LOCK_RETRY_MS, () => {
              cancelRetry = undefined
              askIfVisible()
            })
          }
        }
      )
  }

  const askIfVisible = (): void => {
    if (doc.visibilityState === 'visible') ask()
  }

  doc.addEventListener('visibilitychange', askIfVisible)
  ask()

  return () => {
    if (stopped) return
    stopped = true
    cancelRetry?.()
    cancelRetry = undefined
    doc.removeEventListener('visibilitychange', askIfVisible)
    const sentinel = held
    held = null
    if (sentinel !== null) void sentinel.release().catch(() => undefined)
  }
}

/** The wait after a browser refuses the screen lock. */
export const WAKE_LOCK_RETRY_MS = 5 * 60 * 1000

/** A cancellable timer for a refused lock request. */
export interface WakeLockTimer {
  after(ms: number, run: () => void): () => void
}

const systemTimer: WakeLockTimer = {
  after(ms, run) {
    const id = setTimeout(run, ms)
    return () => clearTimeout(id)
  }
}
